import {
	// autoUpdate,
	flip,
	FloatingFocusManager,
	FloatingPortal,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
} from '@floating-ui/react'
import CleaveInput from 'cleave.js/react'
import { bool, func, oneOf, string } from 'prop-types'
import { Fragment, memo, useMemo, useState } from 'react'
import Calendar from 'react-calendar'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
const StyledCalendar = styled.div`
	background-color: #ffffff;
	border-radius: 14px;
	padding: 12px;
	box-shadow: 0 1px 20px 0 rgba(13, 46, 105, 0.07),
		0 1px 20px 0 rgba(13, 46, 105, 0.07);
	html.dark & {
		background-color: #2e2d2d;
		box-shadow: 0 1px 20px 0px rgba(0, 0, 0, 0.07),
			0 1px 20px 0px rgba(0, 0, 0, 0.07);
	}
	& .react-calendar {
		border: none;
		width: 280px !important;
		& .react-calendar__navigation {
			display: flex;
			margin: 0;
			& button {
				align-items: center;
				background-color: #f6f7f9;
				border-radius: 12px;
				border: none;
				color: #696f85;
				cursor: pointer;
				display: flex;
				font-size: 15px;
				font-weight: 500;
				height: 40px;
				justify-content: center;
				min-width: 40px;
				outline: none;
				padding: 0;
				&:hover,
				&:focus {
					background-color: #018ecc;
					color: #ffffff;
					cursor: pointer;
				}
				&[disabled] {
					color: #696f85;
					cursor: not-allowed;
					&:hover,
					&:focus {
						background-color: #f6f7f9;
						color: #696f85;
					}
				}
			}
			html.dark & button {
				background-color: #222222;
				color: #ffffff;
				&:hover {
					background-color: #018ecc;
					color: #ffffff;
				}
			}
			& .react-calendar__navigation__next-button {
				margin: 0 0 0 8px;
				& svg {
					margin: 2px 0 0 2px;
				}
			}
			& .react-calendar__navigation__prev-button {
				margin: 0 8px 0 0;
				& svg {
					margin: 2px 2px 0 0;
				}
			}
		}
		& .react-calendar__viewContainer {
			& button {
				align-items: center;
				background-color: transparent;
				border: none;
				cursor: pointer;
				display: flex;
				flex-basis: initial !important;
				font-size: 14px;
				font-weight: 500;
				justify-content: center;
				&[disabled] {
					background-color: transparent !important;
					color: #696f85 !important;
					cursor: not-allowed;
				}
			}
			html.dark & button {
				color: #ffffff;
				&:hover {
					color: #ffffff;
				}
			}
			& .react-calendar__month-view__weekdays__weekday {
				align-items: center;
				color: #696f85;
				cursor: default;
				display: flex;
				flex-basis: initial !important;
				font-size: 14px;
				font-weight: 500;
				height: 40px;
				justify-content: center;
				padding: 0;
				text-transform: lowercase;
				text-transform: capitalize;
				width: 40px;
				& abbr {
					text-decoration: none !important;
					overflow: hidden;
				}
			}
			& .react-calendar__month-view__days__day {
				border-radius: 19px;
				height: 38px;
				justify-content: center;
				margin: 1px;
				width: 38px;
				&:hover {
					color: #ffffff;
					background-color: #018ecc;
				}
			}
			& .react-calendar__month-view__days__day--neighboringMonth {
				color: #696f85;
			}
			html.dark & .react-calendar__month-view__days__day--neighboringMonth {
				color: #696f85;
			}
			& .react-calendar__month-view__days__day--weekend {
				color: #ff4921;
			}
			html.dark & .react-calendar__month-view__days__day--weekend {
				color: #ff4921;
			}
			& .react-calendar__year-view__months__month,
			& .react-calendar__decade-view__years__year,
			& .react-calendar__century-view__decades__decade {
				border-radius: 8px;
				height: 35px;
				margin: 10px 0;
				padding: 0 !important;
				width: calc(100% / 3);
				&:hover {
					background-color: #018ecc;
					color: #ffffff;
				}
			}
			& .react-calendar__tile--now {
				&:hover {
					background-color: #018ecc;
				}
			}
			& .react-calendar__tile--hasActive,
			& .react-calendar__tile--active {
				background-color: #018ecc;
				color: #ffffff;
				&:hover {
					background-color: #018ecc;
					color: #ffffff;
				}
			}
		}
	}
`
const StyledControl = styled.div`
	position: relative;
	& input {
		background-color: #ffffff;
		border-color: #e1e1e1;
		border-radius: 10px;
		border-style: solid;
		border-width: 1.5px;
		color: #000000;
		font-weight: 500;
		outline: none;
		width: 100%;
		/* 52, 44, 36 */
		font-size: 16px;
		height: 44px;
		padding-left: 15px;
		padding-right: 45px;
		&[data-size='large'] {
			font-size: 18px;
			height: 52px;
			padding-left: 18px;
			padding-right: 18px;
		}
		&[data-size='small'] {
			font-size: 14px;
			height: 36px;
			padding-left: 12px;
			padding-right: 12px;
		}
		&[data-size='extra-small'] {
			font-size: 12px;
			height: 30px;
			padding-left: 6px;
			padding-right: 6px;
		}
		&[data-hide-border='true'] {
			border-width: 0;
		}
		&[data-error='true'] {
			border-color: #e41d32;
		}
		&[type='password'] {
			letter-spacing: 2px;
		}
		&[type='text'] {
			letter-spacing: initial;
		}
		&:focus {
			border-color: #018ecc;
		}
		&:hover:not(:disabled) {
			border-color: #018ecc;
		}
		&:disabled {
			background-color: #f4f4f4;
			color: #717171;
			cursor: default;
		}
		&::placeholder {
			color: #717171;
			letter-spacing: initial;
		}
		.dark & {
			background-color: #2e2d2d;
			color: #e1e1e1;
			border-color: #555555;
			&[data-error='true']:not(:disabled) {
				border-color: #e41d32;
			}
			&:hover:not(:disabled) {
				border-color: #018ecc;
			}
			&:focus {
				border-color: #018ecc;
			}
			&:disabled {
				color: #717171;
			}
		}
	}
	& .dropdown-toggle {
		align-items: center;
		background-color: transparent;
		color: #808080;
		display: flex;
		justify-content: center;
		position: absolute;
		user-select: none;
		/* 52, 44, 36 */
		border-radius: 18px;
		height: 36px;
		right: 5px;
		top: 4px;
		width: 36px;
		&[data-size='large'] {
			border-radius: 20px;
			height: 40px;
			right: 6px;
			top: 6px;
			width: 40px;
		}
		&[data-size='small'] {
			border-radius: 16px;
			height: 32px;
			right: 5px;
			top: 2px;
			width: 32px;
		}
		&[data-size='extra-small'] {
			border-radius: 13px;
			height: 26px;
			right: 3px;
			top: 1.5px;
			width: 26px;
		}
		&:hover:not([data-disabled='true']) {
			background-color: #f1f1f1;
		}
	}
	& .dropdown-toggle:not([data-disabled='true']) {
		cursor: pointer;
	}
	.dark & .dropdown-toggle {
		&:hover:not([data-disabled='true']) {
			background-color: #222222;
		}
	}
`
const DownIcon = () => (
	<svg fill='none' height='20' viewBox='0 0 20 20' width='20'>
		<path
			d='M6.66667 5.83333V2.5M13.3333 5.83333V2.5M5.83333 9.16667H14.1667M4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V5.83333C17.5 4.91286 16.7538 4.16667 15.8333 4.16667H4.16667C3.24619 4.16667 2.5 4.91286 2.5 5.83333V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5Z'
			stroke='currentColor'
			strokeLinecap='round'
			strokeLinejoin='round'
			strokeWidth='2'
		/>
	</svg>
)
const NextIcon = () => (
	<svg width='15' height='15' viewBox='0 0 18 18' fill='none'>
		<path
			d='M6.375 3.75L11.625 9L6.375 14.25'
			stroke='currentColor'
			strokeLinecap='round'
			strokeLinejoin='round'
			strokeWidth='2'
		/>
	</svg>
)
const PrevIcon = () => (
	<svg width='14' height='14' viewBox='0 0 18 18' fill='none'>
		<path
			d='M11.625 14.25L6.375 9L11.625 3.75'
			stroke='currentColor'
			strokeLinecap='round'
			strokeLinejoin='round'
			strokeWidth='2'
		/>
	</svg>
)
const locales = {
	ru: {
		days: ['Во', 'По', 'Вт', 'Ср', 'Че', 'Пя', 'Су'],
		months: [
			'Январь',
			'Февраль',
			'Март',
			'Апреля',
			'Май',
			'Июнь',
			'Июль',
			'Август',
			'Сентябрь',
			'Октябрь',
			'Ноябрь',
			'Декабрь',
		],
	},
	uz: {
		days: ['Ya', 'Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh'],
		months: [
			'Yanvar',
			'Fevral',
			'Mart',
			'Aprel',
			'May',
			'Iyun',
			'Iyul',
			'Avgust',
			'Sentyabr',
			'Oktyabr',
			'Noyabr',
			'Dekabr',
		],
	},
	en: {
		days: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
		months: [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December',
		],
	},
}
const DateInput = memo(
	({
		'data-cy': dataCY,
		allowFutureDates = false,
		hideBorder = false,
		isDisabled = false,
		isError = false,
		maxDate = '',
		minDate = '',
		onChange,
		onFocus,
		placeholder = '',
		size = 'medium',
		value = '',
	}) => {
		const translation = useTranslation()
		const language = translation?.i18n?.language
		const days = useMemo(() => {
			const days = locales?.[language]?.days
			return days
		}, [language])
		const months = useMemo(() => {
			const months = locales?.[language]?.months
			return months
		}, [language])
		const [calendar, setCalendar] = useState(new Date())
		const [isOpen, setIsOpen] = useState(false)
		const [view, setView] = useState('month')
		const { refs, floatingStyles, context } = useFloating({
			open: isOpen,
			// whileElementsMounted: autoUpdate,
			onOpenChange: open => {
				const calendar = value?.length === 10 ? new Date(value) : new Date()
				setCalendar(calendar)
				setIsOpen(open)
			},
			middleware: [
				offset(0),
				flip({ fallbackAxisSideDirection: 'end' }),
				shift(),
			],
		})
		const click = useClick(context, { enabled: !isDisabled })
		const dismiss = useDismiss(context)
		const role = useRole(context)
		const { getReferenceProps, getFloatingProps } = useInteractions([
			click,
			dismiss,
			role,
		])
		const memoizedValue = useMemo(() => {
			const stringValue = typeof value === 'string' ? value : ''
			const memoizedValue = stringValue.split('-').reverse().join('-')
			return memoizedValue
		}, [value])
		const min = useMemo(() => {
			if (typeof minDate !== 'string') return undefined
			const regex = /^\d{4}-\d{2}-\d{2}$/
			if (!regex.test(minDate)) return undefined
			const date = new Date(minDate)
			const [year, month, day] = minDate.split('-').map(Number)
			const isValid =
				date.getUTCFullYear() === year &&
				date.getUTCMonth() + 1 === month &&
				date.getUTCDate() === day
			return isValid ? date : undefined
		}, [minDate])
		const max = useMemo(() => {
			if (typeof maxDate !== 'string') return undefined
			const regex = /^\d{4}-\d{2}-\d{2}$/
			if (!regex.test(maxDate)) return undefined
			const date = new Date(maxDate)
			const [year, month, day] = maxDate.split('-').map(Number)
			const isValid =
				date.getUTCFullYear() === year &&
				date.getUTCMonth() + 1 === month &&
				date.getUTCDate() === day
			return isValid ? date : undefined
		}, [maxDate])
		return (
			<Fragment>
				<StyledControl ref={refs.setReference}>
					<CleaveInput
						className='date-input'
						data-cy={dataCY}
						data-error={isError}
						data-hide-border={hideBorder}
						data-size={size}
						disabled={isDisabled}
						inputMode='numeric'
						onFocus={onFocus}
						placeholder={placeholder}
						type='text'
						value={memoizedValue}
						options={{
							date: true,
							datePattern: ['d', 'm', 'Y'],
							delimiter: '-',
						}}
						onChange={e => {
							const value = e.target.value
							const input = value.split('-').reverse().join('-')
							if (value?.length === 10) {
								const inputDate = new Date(input).getTime()
								const maxDate = max ? max.getTime() : null
								const minDate = min ? min.getTime() : null
								const newValue =
									inputDate > maxDate && maxDate
										? new Date(maxDate).toISOString().slice(0, 10)
										: inputDate < minDate && minDate
										? new Date(minDate).toISOString().slice(0, 10)
										: input
								onChange(newValue)
								return
							} else {
								onChange(input)
							}
						}}
					/>
					<div
						{...getReferenceProps()}
						className='dropdown-toggle'
						data-disabled={isDisabled}
						data-size={size}
					>
						<DownIcon />
					</div>
				</StyledControl>
				{isOpen && (
					<FloatingPortal id='floating-ui-portal'>
						<FloatingFocusManager
							context={context}
							initialFocus={false}
							modal={false}
						>
							<StyledCalendar
								{...getFloatingProps()}
								ref={refs.setFloating}
								style={floatingStyles}
							>
								<Calendar
									next2Label={null}
									nextLabel={<NextIcon />}
									onChange={calendar => {
										const date = String(calendar.getDate() || '').padStart(
											2,
											'0'
										)
										const month = String(
											calendar.getMonth() + 1 || ''
										).padStart(2, '0')
										const year = String(calendar.getFullYear() || '').padStart(
											2,
											'0'
										)
										const value = `${year}-${month}-${date}`
										setCalendar(calendar)
										setIsOpen(false)
										onChange(value)
									}}
									formatMonth={(_, date) => months[date?.getMonth()]}
									maxDate={max}
									minDate={min}
									onViewChange={({ view }) => setView(view)}
									prev2Label={null}
									prevLabel={<PrevIcon />}
									value={calendar}
									view={view}
									formatShortWeekday={(_, date) => {
										const weekDay = days[date?.getDay()]
										return weekDay
									}}
									navigationLabel={({ view, label, date }) => {
										const month = months[date?.getMonth()]
										const newDate = date?.getFullYear()
										const monthLabel = `${month} ${newDate}`
										const isMonth = view === 'month'
										const newLabel = isMonth ? monthLabel : label
										return newLabel
									}}
								/>
							</StyledCalendar>
						</FloatingFocusManager>
					</FloatingPortal>
				)}
			</Fragment>
		)
	}
)
DateInput.propTypes = {
	'data-cy': string,
	allowFutureDates: bool,
	hideBorder: bool,
	isDisabled: bool,
	isError: bool,
	onChange: func,
	onFocus: func,
	placeholder: string,
	size: oneOf(['large', 'medium', 'small', 'extra-small']),
	value: string,
}
export default DateInput
